import React, { CSSProperties } from 'react';
import { List, useDynamicRowHeight } from 'react-window';
import { Badge, Box, Flex, IconButton, Popover, ScrollArea, Text } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { cx } from 'class-variance-authority';
import { ClearIcon, ExpandMoreIcon } from 'shared/icons';

import { WithId } from './multi-select.view-model';
import { useMultiSelect } from './multi-select.provider';
import './styles/multi-select.css';

export const MultiSelectView = ({
   children,
   placeholder,
}: {
   children: React.ReactNode[];
   placeholder?: string;
}) => {
   const viewModel = useMultiSelect();
   const inputRef = React.useRef<HTMLInputElement | null>(null);
   const rowHeight = useDynamicRowHeight({
      defaultRowHeight: 50,
   });
   return (
      <Popover.Root
         open={viewModel.opened}
         onOpenChange={(open) => {
            if (open) {
               inputRef.current?.focus();
            }
            viewModel.onOpenedChange(open);
         }}
      >
         <Popover.Trigger className="multi-select__trigger" type="button">
            <Box>
               <Flex direction="column" className="multi-select__wrapper-area">
                  <Flex className="multi-select__selected-area" gap="1">
                     {viewModel.values.map((v) => (
                        <Badge key={v.id} className="multi-select__badge" color="blue" highContrast>
                           <Text className="multi-select__badge-value" truncate>
                              {viewModel.getLabel(v)}
                           </Text>
                           <IconButton
                              type="button"
                              className="multi-select__badge-button"
                              variant="surface"
                              color="blue"
                              size="1"
                              onClick={(e) => {
                                 viewModel.deselect(v);
                                 e.stopPropagation();
                                 e.preventDefault();
                              }}
                           >
                              <ClearIcon />
                           </IconButton>
                        </Badge>
                     ))}
                  </Flex>
                  <input
                     ref={inputRef}
                     className="multi-select__search"
                     placeholder={viewModel.values.length > 0 ? undefined : placeholder}
                     value={viewModel.search}
                     onChange={(e) => {
                        viewModel.onSearch(e.target.value);
                     }}
                     onKeyDown={() => {
                        if (!viewModel.opened) {
                           viewModel.onOpenedChange(true);
                        }
                     }}
                  />
               </Flex>

               <Text
                  className={cx(
                     'multi-select__indicator',
                     viewModel.opened && 'multi-select__indicator--opened'
                  )}
                  color="gray"
               >
                  <ExpandMoreIcon width={24} height={24} />
               </Text>
            </Box>
         </Popover.Trigger>

         <Popover.Content
            maxHeight="200px"
            size="1"
            onCloseAutoFocus={(e) => {
               if (viewModel.search) {
                  viewModel.clearSearch();
               }

               e.preventDefault();
            }}
            onOpenAutoFocus={(e) => {
               e.preventDefault();
            }}
         >
            {children.length > 0 ? (
               <ScrollArea type="auto" scrollbars="both" style={{ maxHeight: '160px' }}>
                  <List<{
                     items: React.ReactNode[];
                  }>
                     rowComponent={RowComponent}
                     rowCount={children.length}
                     rowHeight={rowHeight}
                     rowProps={{ items: children }}
                  />
               </ScrollArea>
            ) : (
               <Box>
                  <Text color="gray">Опцій не знайдено</Text>
               </Box>
            )}
         </Popover.Content>
      </Popover.Root>
   );
};

function RowComponent({
   index,
   items,
   style,
}: {
   index: number;
   items: React.ReactNode[];
   style: CSSProperties;
}) {
   return (
      <div className="multi-select__row-item" style={style}>
         {items[index]}
      </div>
   );
}

const MultiSelectOptionView = <TValue extends WithId>({
   value,
   children,
}: {
   value: TValue;
   children: React.ReactNode;
}) => {
   const viewModel = useMultiSelect();
   return (
      <div
         className="multi-select__option"
         onClick={() => {
            viewModel.onSelect(value);
            viewModel.onOpenedChange(false);
         }}
      >
         {children}
      </div>
   );
};

export const MultiSelect = observer(MultiSelectView);
export const MultiSelectOption = observer(MultiSelectOptionView);
